import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { queuedMessage } from "../../offlineQueue";

const BETTER_BALL_HCP_FORMATS = ["Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Fourball Better Ball Stableford", "Fourball Stroke Play"];

export default function BetterBallScoreEntry() {
  const [event, setEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);
  // A pair has ONE scorecard — one gross score per hole, credited to
  // whichever partner actually had the better ball that hole. holeGross
  // holds the single entered value; holeScorer records which partner it
  // belongs to (needed to apply the right handicap strokes).
  const [holeGross, setHoleGross] = useState({});
  const [holeScorer, setHoleScorer] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (selectedPair) loadPairScores(selectedPair); }, [selectedPair?.label]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      if (eventRes.data.course_id) {
        const holesRes = await api.get("/courses/" + eventRes.data.course_id + "/holes");
        setHoles(holesRes.data);
      }

      const groupRes = await api.get("/groups");
      const players = (groupRes.data.players || []).filter(p => p.playing);

      // Build pairs: 2 players per group per team
      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = {};
        const tid = p.team_id || 0;
        if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
        grpMap[p.grp][tid].push(p);
      });

      const pairsArr = [];
      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
        Object.entries(teamGroups).forEach(([tid, members]) => {
          if (members.length >= 2) {
            const p1 = members[0];
            const p2 = members[1];
            const mismatched = members.length !== 2;
            const droppedNames = members.slice(2).map(p => p.name);
            pairsArr.push({
              grp: parseInt(grp),
              label: (mismatched ? "⚠️ " : "") + `Group ${grp}: ${p1.name} / ${p2.name}`,
              p1, p2, mismatched, droppedNames,
            });
          } else if (members.length === 1) {
            pairsArr.push({
              grp: parseInt(grp),
              label: `⚠️ Group ${grp}: ${members[0].name}`,
              p1: members[0], p2: null, mismatched: true, droppedNames: [],
            });
          }
        });
      });

      setPairs(pairsArr);
      if (pairsArr.length > 0) setSelectedPair(pairsArr[0]);
      else setLoading(false);
    } catch(e) { console.error(e); setLoading(false); }
  };

  // Loads whichever partner already has a saved score for each hole (that's
  // who gets credit); defaults to p1 for holes with nothing entered yet.
  const loadPairScores = async (pair) => {
    if (!event) return;
    setLoading(true);
    try {
      const scoresRes = await api.get("/scores/" + event.id);
      const gross = {}, scorer = {};
      holes.forEach(h => {
        const s1 = scoresRes.data.find(s => pair.p1 && s.player_id === pair.p1.player_id && s.hole === h.hole && s.gross);
        const s2 = scoresRes.data.find(s => pair.p2 && s.player_id === pair.p2.player_id && s.hole === h.hole && s.gross);
        if (s1) { gross[h.hole] = s1.gross; scorer[h.hole] = "p1"; }
        else if (s2) { gross[h.hole] = s2.gross; scorer[h.hole] = "p2"; }
        else { scorer[h.hole] = "p1"; }
      });
      setHoleGross(gross);
      setHoleScorer(scorer);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getShots = (player, hole) => {
    if (!player) return 0;
    // Better Ball plays off 85% of the player's Course Handicap (hcp_adj —
    // WHS HCP already adjusted for course slope/rating and capped, see
    // utils/handicap.js), never the raw WHS HCP directly.
    const rawHcp = (player.hcp_adj ?? player.handicap) || 0;
    const hcp = BETTER_BALL_HCP_FORMATS.includes(event?.game_type) ? Math.round(rawHcp * 0.85) : rawHcp;
    const base = Math.floor(hcp / 18);
    const extra = hcp % 18;
    return base + (hole.stroke_index <= extra ? 1 : 0);
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  // Visible, verifiable playing handicap — the actual number strokes are
  // worked out from, so it's obvious the 85% reduction is really happening.
  const playingHcp = (player) => {
    if (!player) return 0;
    const rawHcp = (player.hcp_adj ?? player.handicap) || 0;
    return BETTER_BALL_HCP_FORMATS.includes(event?.game_type) ? Math.round(rawHcp * 0.85) : rawHcp;
  };

  const getHoleStats = (hole) => {
    const gross = parseInt(holeGross[hole.hole]);
    if (!gross) return { net: "", points: "" };
    const scorerKey = holeScorer[hole.hole] || "p1";
    const player = scorerKey === "p1" ? selectedPair?.p1 : selectedPair?.p2;
    const shots = getShots(player, hole);
    const net = gross - shots;
    const points = Math.max(0, 2 + (hole.par - net));
    return { net, points };
  };

  const getRunningTotal = (upToIndex) => {
    let total = 0;
    for (let i = 0; i <= upToIndex; i++) {
      const h = holes[i];
      if (!h) continue;
      const { net, points } = getHoleStats(h);
      if (isMedal) { if (net !== "") total += net; }
      else if (points !== "") total += points;
    }
    return total || (total === 0 ? 0 : "");
  };

  const pairTotal = holes.length ? getRunningTotal(holes.length - 1) : "";

  const handleScoreChange = (hole, val) => {
    setHoleGross(prev => ({ ...prev, [hole]: val }));
  };

  const handleScorerToggle = (hole, which) => {
    setHoleScorer(prev => ({ ...prev, [hole]: which }));
  };

  const buildPairSaveScores = () => {
    const allScores = [];
    holes.forEach(h => {
      const gross = parseInt(holeGross[h.hole]);
      const scorerKey = holeScorer[h.hole] || "p1";
      if (selectedPair.p1) allScores.push({ player_id: selectedPair.p1.player_id, hole: h.hole, gross: (gross && scorerKey === "p1") ? gross : 0 });
      if (selectedPair.p2) allScores.push({ player_id: selectedPair.p2.player_id, hole: h.hole, gross: (gross && scorerKey === "p2") ? gross : 0 });
    });
    return allScores;
  };

  const saveScores = async () => {
    if (!event || !selectedPair) return;
    try {
      const res = await api.post("/scores/save", { event_id: event.id, scores: buildPairSaveScores(), publish: false });
      showToast(queuedMessage(res, "Scores saved!"));
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const randomScores = () => {
    if (!selectedPair) return;
    const gross = {}, scorer = {};
    holes.forEach(h => {
      gross[h.hole] = h.par + Math.floor(Math.random() * 3) - 1;
      scorer[h.hole] = Math.random() < 0.5 ? "p1" : "p2";
    });
    setHoleGross(gross);
    setHoleScorer(scorer);
    showToast("Random scores generated");
  };

  const randomAllGroups = async () => {
    if (!event || pairs.length === 0) return;
    try {
      const allScores = [];
      pairs.forEach(pair => {
        holes.forEach(h => {
          const g = h.par + Math.floor(Math.random() * 3) - 1;
          const scorerKey = Math.random() < 0.5 ? "p1" : "p2";
          if (pair.p1) allScores.push({ player_id: pair.p1.player_id, hole: h.hole, gross: scorerKey === "p1" ? g : 0 });
          if (pair.p2) allScores.push({ player_id: pair.p2.player_id, hole: h.hole, gross: scorerKey === "p2" ? g : 0 });
        });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      showToast(queuedMessage(res, "Random scores generated for all " + pairs.length + " groups"));
      if (selectedPair) loadPairScores(selectedPair);
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const scorerName = (key) => (key === "p1" ? selectedPair?.p1?.name : selectedPair?.p2?.name)?.split(" ")[0] || "";

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">{event?.game_type || "Better Ball"} Score Entry</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={saveScores} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Scores</button>
            <button type="button" onClick={randomScores} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random Scores</button>
            <button type="button" onClick={randomAllGroups} className="bg-pink-600 hover:bg-pink-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random ALL Groups</button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-56 flex-shrink-0 border-r border-gray-300 bg-yellow-50 overflow-y-auto">
            <div className="px-3 py-2 text-sm font-extrabold text-green-900 uppercase tracking-wide sticky top-0 bg-yellow-200 border-b-2 border-green-800">Every Pair Playing</div>
            <div className="flex flex-col">
              {pairs.map((p, i) => (
                <button key={p.label} type="button" onClick={() => setSelectedPair(p)}
                  className={"text-left text-xs font-semibold px-3 py-2 border-b border-yellow-100 " +
                    (selectedPair?.label === p.label
                      ? "bg-blue-600 text-white"
                      : (i % 2 === 0 ? "bg-white" : "bg-yellow-100") + " text-gray-700 hover:bg-blue-50")}>
                  {p.label}
                </button>
              ))}
              {pairs.length === 0 && <div className="px-3 py-4 text-xs text-gray-500">No pairs found.</div>}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4 flex-wrap">
              {selectedPair && selectedPair.p1 && selectedPair.p2 && (
                <span className="text-xs text-gray-600">
                  {selectedPair.p1.name} (Course HCP {selectedPair.p1.hcp_adj ?? selectedPair.p1.handicap} &times; 85% &rarr; <b>{playingHcp(selectedPair.p1)} playing</b>) &nbsp;|&nbsp; {selectedPair.p2.name} (Course HCP {selectedPair.p2.hcp_adj ?? selectedPair.p2.handicap} &times; 85% &rarr; <b>{playingHcp(selectedPair.p2)} playing</b>)
                </span>
              )}
              <div className="ml-auto bg-green-800 text-white rounded-lg px-5 py-2 text-center">
                <div className="text-[10px] uppercase tracking-wide text-green-200">{isMedal ? "Total Net" : "Total Points"}</div>
                <div className="text-2xl font-extrabold leading-none">{pairTotal}</div>
              </div>
            </div>

            {selectedPair?.mismatched && (
              <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 text-xs text-yellow-800 font-medium">
                ⚠️ This group isn't split 2-and-2 by pair — check pairing on the Pairs page.
                {selectedPair.droppedNames.length > 0 && ` Not getting scores entered here: ${selectedPair.droppedNames.join(", ")}.`}
              </div>
            )}

            {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading...</div>}

            <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-3 py-2 text-left w-12">Hole</th>
                <th className="px-3 py-2 text-center w-12">Par</th>
                <th className="px-3 py-2 text-center w-12">SI</th>
                <th className="px-3 py-2 text-center w-40">Best Gross</th>
                <th className="px-3 py-2 text-center w-48">Whose Score</th>
                <th className="px-3 py-2 text-center w-16">Net</th>
                {!isMedal && <th className="px-3 py-2 text-center w-16">Points</th>}
                <th className="px-3 py-2 text-center w-20">Running</th>
              </tr>
            </thead>
            <tbody>
              {holes.map((hole, i) => {
                const { net, points } = getHoleStats(hole);
                const running = getRunningTotal(i);
                const scorerKey = holeScorer[hole.hole] || "p1";
                return (
                  <tr key={hole.hole} className={"border-b border-gray-200 " + (i%2===0 ? "bg-white" : "bg-green-50")}>
                    <td className="px-3 py-1.5 font-medium">{hole.hole}</td>
                    <td className="px-3 py-1.5 text-center">{hole.par}</td>
                    <td className="px-3 py-1.5 text-center text-gray-500">{hole.stroke_index}</td>
                    <td className="px-3 py-1.5 text-center">
                      <input type="number" min="1" max="15"
                        value={holeGross[hole.hole] || ""}
                        onChange={e => handleScoreChange(hole.hole, e.target.value)}
                        className="border border-gray-300 rounded px-1 py-0.5 w-16 text-xs text-center focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {selectedPair?.p1 && (
                          <button type="button" onClick={() => handleScorerToggle(hole.hole, "p1")}
                            className={"px-2 py-1 rounded text-[11px] font-semibold border " + (scorerKey === "p1" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50")}>
                            {selectedPair.p1.name.split(" ")[0]}
                          </button>
                        )}
                        {selectedPair?.p2 && (
                          <button type="button" onClick={() => handleScorerToggle(hole.hole, "p2")}
                            className={"px-2 py-1 rounded text-[11px] font-semibold border " + (scorerKey === "p2" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50")}>
                            {selectedPair.p2.name.split(" ")[0]}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-center">{net}</td>
                    {!isMedal && <td className="px-3 py-1.5 text-center font-bold text-blue-700">{points}</td>}
                    <td className="px-3 py-1.5 text-center font-bold">{running}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
              {!loading && holes.length === 0 && (
                <div className="text-center py-8 text-gray-500">No course loaded. Go to Course page and select a course.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
